import { redirect } from '@tanstack/react-router'
import axios from 'axios'
import { apiClient } from '@/api/client'
import { useAuthStore, type User } from '@/stores/auth'
import { useWorkspaceStore, type Workspace } from '@/stores/workspace'

/**
 * Story E02.05 — `_authenticated` bootstrap loader (D-03 workaround).
 *
 * Why this exists
 * ---------------
 * Go has no `/api/auth/me` (decisions-log D-03). On a cold mount (F5,
 * direct URL paste, new tab) the auth-store is empty even though the
 * httpOnly auth cookie may still be valid. We can't read the cookie from
 * JS — the only way to know if it's alive is to make an authenticated
 * request and see if it succeeds.
 *
 * Strategy
 * --------
 *   1. Fast-path: `useAuthStore.isAuthenticated === true` → return immediately.
 *      In-SPA navigations between authenticated routes hit this path and
 *      pay zero network cost (architecture §5.3).
 *   2. Cold mount: `GET /api/workspaces` (cheap, ws-independent endpoint).
 *      - 200 + `data.workspaces` → cookie is alive. Hydrate
 *        `useWorkspaceStore.workspaces`. If the response carries `data.user`
 *        (Python parity, R-E2-2) → `setUser(data.user)`. Otherwise install
 *        a stub-user with `role: 'user'` so admin-guard rejects /admin
 *        defensively (AC-06).
 *      - 401 → cookie dead. Throw `redirect({ to: '/login', search: { returnTo } })`
 *        (the response interceptor's refresh attempt has already happened
 *        upstream; reaching here means even refresh failed).
 *      - other failure (network, 5xx) → throw `redirect` with `error: 'network'`.
 *   3. If a persisted `activeWsId` is no longer in the returned workspaces
 *      list (workspace deleted while user was offline) → switch to the first
 *      one or null (AC-02).
 *
 * Singleflight (AC-03)
 * --------------------
 * Two concurrent route mounts (e.g. parallel browser tabs sharing a process,
 * or React strict-mode double-invocation) MUST NOT both hit `/workspaces`.
 * We coalesce on a module-level `inflight` promise; the second caller awaits
 * the first. On rejection we clear the slot so the next mount can retry.
 */

/**
 * Last-resort fallback installed when `/api/auth/me` does not return a body —
 * which should not happen in practice now that Go ships the endpoint, but is
 * kept defensively so a transient empty response cannot crash the SPA.
 * `role: 'user'` is intentional fail-closed (admin-guard rejects, AC-06).
 */
const STUB_USER: User = {
  username: '?',
  realUsername: '?',
  role: 'user',
  userCode: '',
  activeWorkspaceId: null,
  workspaceName: null,
}

type WorkspacesResponse = {
  workspaces: Workspace[]
  // Optional — present iff Go-side eventually decides to attach the caller
  // to the workspaces response. Not relied on (R-E2-2).
  user?: User
}

interface RawMeResponse {
  id: number
  email: string
  username: string
  role: string
  status: string
  user_code: string
}

let inflight: Promise<void> | null = null

/**
 * Reset the singleflight slot. Test-only — production code should never
 * need to bypass coalescing.
 */
export function __resetBootstrapForTests(): void {
  inflight = null
}

/**
 * Bootstrap the authenticated session for `pathname`. Throws a TanStack
 * Router `redirect(...)` on auth failure; the caller (`_authenticated.tsx`'s
 * `beforeLoad`) lets it propagate so the router navigates without mounting
 * the destination.
 */
export async function bootstrapAuth(pathname: string): Promise<void> {
  // Fast-path: already authenticated in this tab — nothing to verify.
  if (useAuthStore.getState().isAuthenticated) {
    return
  }

  if (!inflight) {
    inflight = doBootstrap(pathname).finally(() => {
      inflight = null
    })
  }
  await inflight
}

async function doBootstrap(pathname: string): Promise<void> {
  let res
  try {
    res = await apiClient.get<WorkspacesResponse>('/workspaces')
  } catch (err) {
    // The response interceptor already attempted ONE silent refresh on 401
    // (client.ts singleflight). If we still see 401 here, refresh failed
    // and the user must re-authenticate.
    const status = readErrorStatus(err)
    if (status === 401) {
      throw redirect({
        to: '/login',
        search: { returnTo: pathname },
      })
    }
    // Network error / 5xx — bounce to login with an error marker so the
    // page can surface "сервер недоступний" (AC-05). LoginPage may choose
    // to read this; if it doesn't, the redirect itself is still the
    // safe default.
    throw redirect({
      to: '/login',
      search: { returnTo: pathname, error: 'network' },
    })
  }

  // Defensive: the interceptor returned a wrapped error on transport failure;
  // reaching here means we have a 2xx response. Trust the body shape but
  // tolerate either `{ workspaces: [...] }` or a raw array (older Python
  // parity — Python returns a bare list).
  const body = res.data
  const workspaces: Workspace[] = Array.isArray(body)
    ? (body as Workspace[])
    : (body?.workspaces ?? [])

  useWorkspaceStore.getState().setWorkspaces(workspaces)

  // Prefer user attached to /workspaces (R-E2-2 — older Python parity).
  // Otherwise hit the new Go `/auth/me` endpoint to learn who we are.
  // STUB_USER is the last-resort fallback if both paths fail.
  const userFromResponse = !Array.isArray(body) ? body?.user : undefined
  let resolvedUser: User | null = userFromResponse ?? null
  if (!resolvedUser) {
    try {
      const me = await apiClient.get<RawMeResponse>('/auth/me')
      const raw = me.data
      resolvedUser = {
        username: raw.username,
        realUsername: raw.username,
        role: raw.role === 'admin' ? 'admin' : 'user',
        userCode: raw.user_code ?? '',
        activeWorkspaceId: null,
        workspaceName: null,
      }
    } catch {
      // /auth/me unavailable or failed — fall through to defensive stub.
    }
  }
  useAuthStore.getState().setUser(resolvedUser ?? STUB_USER)

  // If persisted activeWsId points at a workspace the user no longer has
  // access to (deleted, kicked), reset to the first available or null.
  // Personal context (activeWsId === null) is ALWAYS valid — it never matches a
  // workspace id, so it must be treated as valid explicitly, otherwise every
  // reload in personal context would wrongly fall back to workspaces[0].
  const persistedId = useWorkspaceStore.getState().activeWsId
  const stillValid =
    persistedId === null || workspaces.some((w) => w.id === persistedId)
  if (!stillValid) {
    const fallback = workspaces[0]?.id ?? null
    useWorkspaceStore.getState().switchWorkspace(fallback)
  }
}

function readErrorStatus(err: unknown): number | undefined {
  if (axios.isAxiosError(err)) return err.response?.status
  // `normalizeError` envelope returned by the response interceptor.
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status?: unknown }).status
    return typeof s === 'number' ? s : undefined
  }
  return undefined
}
